import React from "react";
import { AutoComplete } from "../autoComplete/autoComplete";
import { range } from "lodash";
import styles from "./timePicker.css";
import * as CrossSvg from "../../../assets/cross_icon.svg";
import { IconButton } from "../../inputs/buttons/iconButton/iconButton";
import { durationToSeconds, secondsToDuration } from "./timePickerUtils";
import { intl } from "../../../i18n/TypedIntl";
import classNames from "classnames";

interface ITimeFieldConfig {
  hidden?: boolean;
  disabled?: boolean;
  provider?: () => number[];
}

export interface ITimePickerProps {
  onTimePicked?: (time: number | undefined) => void;
  onReset?: () => void;
  time?: number | undefined;
  min?: Duration;
  max?: Duration;
  disabled?: boolean;
  invalid?: boolean;
  hoursConfig?: ITimeFieldConfig;
  minutesConfig?: ITimeFieldConfig;
}

export const TimePicker = (props: ITimePickerProps) => {
  const duration = secondsToDuration(props.time);

  const handleTimePicked = (key: keyof Duration) => (value: number | null | "") => {
    const mappedValue = value || undefined;
    if (duration === undefined) {
      return props.onTimePicked?.(durationToSeconds({ [key]: mappedValue }));
    }
    duration[key] = mappedValue;
    props.onTimePicked?.(durationToSeconds(duration));
  };

  return (
    <div className={styles.container}>
      {!props.hoursConfig?.hidden && (
        <AutoComplete<number>
          isOpen={props.disabled || props.hoursConfig?.disabled ? false : undefined}
          className={styles.input}
          inputContainerClassName={classNames({
            [styles.disabled]: props.disabled || props.hoursConfig?.disabled
          })}
          itemsProvider={props.hoursConfig?.provider ?? (() => range(0, 24))}
          inputValue={
            duration?.hours !== undefined && duration.hours !== null
              ? intl.formatMessage("timePicker.hours.shorthand", {
                  hours: duration.hours
                })
              : intl.formatMessage("timePicker.hours.placeholder")
          }
          itemToString={(item) =>
            intl.formatMessage("timePicker.hours.shorthand", {
              hours: item
            })
          }
          onChange={handleTimePicked("hours")}
          hasError={props?.invalid}
          readOnly
        />
      )}
      {!props.minutesConfig?.hidden && (
        <AutoComplete<number>
          isOpen={props.disabled || props.minutesConfig?.disabled ? false : undefined}
          className={styles.input}
          inputContainerClassName={classNames({
            [styles.disabled]: props.disabled || props.minutesConfig?.disabled
          })}
          itemsProvider={props.minutesConfig?.provider ?? (() => range(0, 60))}
          inputValue={
            duration?.minutes !== undefined && duration.minutes !== null
              ? intl.formatMessage("timePicker.minutes.shorthand", {
                  minutes: duration.minutes
                })
              : intl.formatMessage("timePicker.minutes.placeholder")
          }
          itemToString={(item) =>
            intl.formatMessage("timePicker.minutes.shorthand", {
              minutes: item
            })
          }
          onChange={handleTimePicked("minutes")}
          hasError={props?.invalid}
          readOnly
        />
      )}
      {props.onReset && (
        <IconButton src={CrossSvg} onClick={props.onReset} disabled={props.disabled} />
      )}
    </div>
  );
};
